import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";

import { useEffect } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { CookieConsentBanner } from "~/components/CookieConsent";
import { CookieSettingsButton } from "~/components/CookieSettingsButton";
import { CookieComplianceManager } from "~/components/CookieComplianceManager";
import { Layout } from "~/components/Layout";
import { preferenceManager } from "~/lib/preference-manager";
import { cookieManager, CookieCategory } from "~/lib/cookie-manager";
import useAnalytics from "~/hooks/useAnalytics";

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootComponent,
});

function RootComponent() {
  const analytics = useAnalytics();

  // Initialize preferences on app start
  useEffect(() => {
    // Initialize user preferences (theme, language, etc.)
    preferenceManager.initialize();
  }, []);

  // Track app initialization only after consent verification
  useEffect(() => {
    // Only track if consent has been set and analytics is allowed
    if (cookieManager.hasConsentBeenSet() &&
        cookieManager.isCategoryConsented(CookieCategory.ANALYTICS)) {
      analytics.trackUserJourney('app_start', {
        timestamp: Date.now(),
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`
      });
    }
  }, [analytics]);

  // Listen for consent changes to track delayed app start if user accepts later
  useEffect(() => {
    const handleConsentChange = () => {
      // If user just accepted analytics consent, track the app start
      if (cookieManager.isCategoryConsented(CookieCategory.ANALYTICS)) {
        analytics.trackUserJourney('app_start_delayed', {
          timestamp: Date.now(),
          user_agent: navigator.userAgent,
          screen_resolution: `${screen.width}x${screen.height}`,
          viewport_size: `${window.innerWidth}x${window.innerHeight}`,
          consent_timing: 'delayed'
        });
      }
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => window.removeEventListener('cookieConsentChanged', handleConsentChange);
  }, [analytics]);

  return (
    <div className="bg-background text-foreground">
      <Layout>
        <Outlet />
      </Layout>

      {/* Cookie Management Components */}
      <CookieConsentBanner />
      <CookieSettingsButton position="bottom-left" />
      {/* Debug component only in development */}
      {process.env.NODE_ENV === 'development' && <CookieComplianceManager />}

      {/* Development Tools */}
      <ReactQueryDevtools buttonPosition="top-left" />
      <TanStackRouterDevtools position="bottom-right" />
    </div>
  );
}
